import { CreateLandingExceptionButton } from "@/components/landing/CreateLandingExceptionButton";
import { UpdateLandingExceptionDialog } from "@/components/landing/UpdateLandingExceptionDialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import {
  AirlineExceptionCriteria,
  LandingFeeException,
  TailNumberExceptionCriteria,
  useDeleteLandingFeeException,
  useLandingFeeExceptions,
  useToggleLandingFeeExceptionStatus,
  WeightExceptionCriteria,
} from "@/queries/landing";
import { format, isBefore, isValid, parseISO } from "date-fns";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { useState } from "react";

// --- Formatting Helpers ---

const formatFee = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return "Free";
  return `$${(cents / 100).toFixed(2)}`;
};

const formatCriteriaType = (type: string): string => {
  switch (type?.toLowerCase()) {
    case "airlineexceptioncriteria":
      return "Airline";
    case "weightexceptioncriteria":
      return "Weight";
    case "tailnumberexceptioncriteria":
      return "Tail Number";
    default:
      return type || "Unknown";
  }
};

const formatCriteriaDetails = (exc: LandingFeeException): string => {
  switch (exc.criteria_type?.toLowerCase()) {
    case "airlineexceptioncriteria":
      const airlineCrit = exc.criteria as AirlineExceptionCriteria;
      return `IATA: ${airlineCrit.airline_code_iata}`;
    case "weightexceptioncriteria":
      const weightCrit = exc.criteria as WeightExceptionCriteria;
      let details = [];
      if (
        weightCrit.min_weight_lbs !== null &&
        weightCrit.min_weight_lbs !== undefined
      ) {
        details.push(`Min: ${weightCrit.min_weight_lbs.toLocaleString()} lbs`);
      }
      if (
        weightCrit.max_weight_lbs !== null &&
        weightCrit.max_weight_lbs !== undefined
      ) {
        details.push(`Max: ${weightCrit.max_weight_lbs.toLocaleString()} lbs`);
      }
      return details.join(", ") || "Any Weight";
    case "tailnumberexceptioncriteria":
      const tailNumberCrit = exc.criteria as TailNumberExceptionCriteria;
      return `Tail #: ${tailNumberCrit.tail_number}`;
    default:
      return JSON.stringify(exc.criteria);
  }
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, "MMM d, yyyy");
    }
  } catch (e) {}
  // Fallback for potentially non-ISO strings like YYYY-MM-DD
  const parts = dateString.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      const date = new Date(Date.UTC(year, month, day));
      if (isValid(date)) {
        return format(date, "MMM d, yyyy");
      }
    }
  }
  return dateString; // Return original string if parsing fails
};

const isExceptionArchived = (
  effectiveUntil: string | null | undefined
): boolean => {
  if (!effectiveUntil) return false;
  try {
    const date = parseISO(effectiveUntil);
    if (isValid(date)) {
      return isBefore(date, new Date());
    }
  } catch (e) {}
  // Fallback for potentially non-ISO strings like YYYY-MM-DD
  const parts = effectiveUntil.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      const date = new Date(Date.UTC(year, month, day));
      if (isValid(date)) {
        return isBefore(date, new Date());
      }
    }
  }
  return false;
};

interface ExceptionItemProps {
  exception: LandingFeeException;
  onStatusToggle: (id: number, currentStatus: string) => void;
  isToggling: boolean;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}

const ExceptionItem: React.FC<ExceptionItemProps> = ({
  exception,
  onStatusToggle,
  isToggling,
  onDelete,
  isDeleting,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
      <div className="border rounded-md p-4 space-y-1 relative">
        <div className="absolute top-2 right-2 flex gap-2">
          <UpdateLandingExceptionDialog exception={exception} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Delete Exception</span>
          </Button>
        </div>
        <div className="flex flex-wrap md:flex-nowrap items-center gap-4">
          <div className="flex-1 space-y-1">
            <h3 className="font-semibold text-lg mr-10">{exception.name}</h3>
            <Label
              htmlFor={`status-switch-${exception.id}`}
              className="flex items-center space-x-2 pt-1 cursor-pointer"
            >
              <Switch
                id={`status-switch-${exception.id}`}
                checked={exception.status === "ACTIVE"}
                onCheckedChange={() =>
                  onStatusToggle(exception.id, exception.status)
                }
                disabled={isToggling}
                aria-label={`Toggle status for ${exception.name}`}
              />
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
                  exception.status === "ACTIVE"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {exception.status}
              </span>
            </Label>
            {exception.description && (
              <p className="text-sm text-muted-foreground pt-1">
                {exception.description}
              </p>
            )}
          </div>

          <div className="flex-1 space-y-1">
            <p className="text-sm">
              <strong>Type:</strong>{" "}
              {formatCriteriaType(exception.criteria_type)}
            </p>
            <p className="text-sm">
              <strong>Fee:</strong> {formatFee(exception.fixed_fee_cents)}
            </p>
          </div>

          <div className="flex-1 space-y-1">
            <p className="text-sm">
              <strong>Details:</strong> {formatCriteriaDetails(exception)}
            </p>
            <div className="flex flex-col sm:flex-row sm:space-x-4 text-sm text-muted-foreground pt-1">
              <span>
                <strong>Effective from:</strong>{" "}
                {formatDate(exception.effective_from)}
              </span>
              <span>
                <strong>Until:</strong> {formatDate(exception.effective_until)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Landing Fee Exception</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{exception.name}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(exception.id);
                setShowDeleteConfirm(false);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export function LandingFeeExceptions() {
  const [showArchived, setShowArchived] = useState(false);
  const { toast } = useToast();

  const {
    data: exceptionsData,
    isLoading: isLoadingExceptions,
    error: errorExceptions,
  } = useLandingFeeExceptions();

  const toggleStatusMutation = useToggleLandingFeeExceptionStatus();
  const deleteMutation = useDeleteLandingFeeException();

  const handleStatusToggle = (id: number, currentStatus: string) => {
    toggleStatusMutation.mutate({ id });
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Exception deleted successfully.",
          });
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Error",
            description: error.message,
          });
        },
      }
    );
  };

  // Separate active and archived exceptions
  const activeExceptions =
    exceptionsData?.filter(
      (exc) => !isExceptionArchived(exc.effective_until)
    ) || [];
  const archivedExceptions =
    exceptionsData?.filter((exc) => isExceptionArchived(exc.effective_until)) ||
    [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Landing Fee Exceptions</CardTitle>
          <CardDescription>
            View and manage specific exceptions to standard landing fees.
          </CardDescription>
        </div>
        <CreateLandingExceptionButton />
      </CardHeader>
      <CardContent>
        {isLoadingExceptions && <p>Loading exceptions...</p>}
        {errorExceptions && (
          <p className="text-red-600">
            Error loading exceptions: {errorExceptions.message}
          </p>
        )}
        {!isLoadingExceptions && !errorExceptions && (
          <div className="space-y-4">
            {activeExceptions.length > 0 && (
              <>
                <h2 className="font-semibold text-lg">Active Exceptions</h2>
                {activeExceptions.map((exc) => (
                  <ExceptionItem
                    key={exc.id}
                    exception={exc}
                    onStatusToggle={handleStatusToggle}
                    isToggling={toggleStatusMutation.isPending}
                    onDelete={handleDelete}
                    isDeleting={deleteMutation.isPending}
                  />
                ))}
              </>
            )}
            {archivedExceptions.length > 0 && (
              <>
                <div className="flex items-center gap-2 pt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowArchived(!showArchived)}
                    className="flex items-center gap-2"
                  >
                    {showArchived ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold">
                      Archived Exceptions ({archivedExceptions.length})
                    </span>
                  </Button>
                </div>
                {showArchived && (
                  <div className="space-y-4">
                    {archivedExceptions.map((exc) => (
                      <ExceptionItem
                        key={exc.id}
                        exception={exc}
                        onStatusToggle={handleStatusToggle}
                        isToggling={toggleStatusMutation.isPending}
                        onDelete={handleDelete}
                        isDeleting={deleteMutation.isPending}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
            {activeExceptions.length === 0 &&
              archivedExceptions.length === 0 && (
                <p className="text-center text-muted-foreground">
                  No landing fee exceptions found.
                </p>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
